<script setup lang="ts">
/**
 * The signed-out layout.
 *
 * It carries the same four landmarks as the authenticated shell, including a
 * `navigation` landmark, because `FND-11` says "on every route" and the login
 * screen is a route. A landmark set that changes shape between layouts is exactly
 * the kind of inconsistency a screen-reader user notices and a sighted reviewer
 * does not.
 *
 * No `AppNav` here: there is no role, so there is nothing role-aware to show.
 */

import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import ThemeMenu from './ThemeMenu.vue'

import brandMark from '@/assets/MatchPointIcon.svg'
import MpToaster from '@/components/base/MpToaster.vue'
import ErrorBoundary from '@/components/state/ErrorBoundary.vue'
import { useMainFocus } from '@/lib/route-focus'
import { SCREENS_BY_ID } from '@/screens/catalog'
import { useSessionStore } from '@/stores/session'

const route = useRoute()
const session = useSessionStore()

const screen = computed(() => SCREENS_BY_ID[route.meta.screenId])

const main = ref<HTMLElement | null>(null)
useMainFocus(main)
</script>

<template>
  <div class="mp-public">
    <a
      class="mp-sr-only"
      href="#mp-main"
    >Skip to main content</a>

    <header
      class="mp-public__header"
      role="banner"
    >
      <RouterLink
        class="mp-public__brand"
        to="/"
      >
        <!-- Decorative: the link's own text already names the destination, so
             announcing the mark would only double it. -->
        <img
          class="mp-public__mark"
          :src="brandMark"
          alt=""
          aria-hidden="true"
        >
        Match&nbsp;Point
      </RouterLink>

      <div class="mp-public__actions">
        <ThemeMenu />
        <nav
          class="mp-public__nav"
          role="navigation"
          aria-label="Account"
        >
          <RouterLink
            v-if="session.isAuthenticated"
            to="/home"
          >
            Go to the app
          </RouterLink>
          <template v-else>
            <RouterLink to="/login">
              Sign in
            </RouterLink>
            <RouterLink to="/signup">
              Create an account
            </RouterLink>
          </template>
        </nav>
      </div>
    </header>

    <main
      id="mp-main"
      ref="main"
      class="mp-public__main"
      role="main"
      tabindex="-1"
      :aria-label="screen.title"
    >
      <div class="mp-public__content">
        <ErrorBoundary>
          <RouterView />
        </ErrorBoundary>
      </div>
    </main>

    <footer
      class="mp-public__footer"
      role="contentinfo"
    >
      <p>Match Point · the behavioral-health treatment directory</p>
      <nav aria-label="Legal">
        <RouterLink to="/legal">
          Terms and privacy
        </RouterLink>
      </nav>
    </footer>

    <MpToaster />
  </div>
</template>

<style scoped>
.mp-public {
  display: flex;
  flex-direction: column;
  min-block-size: 100vh;
  min-block-size: 100dvh;
}

.mp-public__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-5);
  border-block-end: 1px solid var(--border-subtle);
  background: var(--surface-raised);
}

.mp-public__brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-family: var(--font-heading);
  font-size: var(--font-size-h4);
  font-weight: var(--heading-weight);
  letter-spacing: var(--heading-tracking);
  color: var(--text-primary);
  text-decoration: none;
}

.mp-public__brand:visited {
  color: var(--text-primary);
}

/* Sized off the heading's own line box so the mark tracks the wordmark through
   both density scales rather than being pinned to one pixel value. */
.mp-public__mark {
  flex: none;
  inline-size: 1.6em;
  block-size: 1.6em;
}

.mp-public__actions {
  display: flex;
  align-items: center;
  gap: var(--space-5);
}

.mp-public__nav {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  font-size: var(--font-size-small);
  font-weight: 600;
}

.mp-public__main {
  flex: 1;
  padding: var(--space-10) var(--space-5);
}

.mp-public__main:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
}

.mp-public__content {
  max-inline-size: 64rem;
  margin-inline: auto;
}

.mp-public__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  background: var(--surface-sunken);
  border-block-start: 1px solid var(--border-subtle);
  font-size: var(--font-size-caption);
  color: var(--text-muted);
}

@media (max-width: 767px) {
  .mp-public__main {
    padding: var(--space-6) var(--space-4);
  }
}
</style>
